// src/pages/Products.js
import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence, Reorder } from 'framer-motion';
import toast from 'react-hot-toast';
import { FaEdit, FaTrash, FaStar, FaChevronLeft, FaChevronRight, FaPlus, FaBox, FaGripVertical, FaSort, FaSave, FaTimes } from 'react-icons/fa';
import ProductForm from '../components/ProductForm';

const Products = () => {
  const [products, setProducts] = useState([]);
  const [editingProduct, setEditingProduct] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [imageIndexes, setImageIndexes] = useState({});
  const [loading, setLoading] = useState(true);

  // Reorder state
  const [reorderMode, setReorderMode] = useState(false);
  const [reorderList, setReorderList] = useState([]);
  const [savingOrder, setSavingOrder] = useState(false);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/products`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to load');
      const data = await res.json();
      setProducts(data);
    } catch (err) {
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/products/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Delete failed');
      fetchProducts();
      toast.success('Product deleted successfully');
    } catch (err) {
      toast.error('Failed to delete product');
    }
  };

  const nextImage = (productId, imagesLength) => {
    setImageIndexes(prev => ({
      ...prev,
      [productId]: ((prev[productId] || 0) + 1) % imagesLength
    }));
  };

  const prevImage = (productId, imagesLength) => {
    setImageIndexes(prev => ({
      ...prev,
      [productId]: ((prev[productId] || 0) - 1 + imagesLength) % imagesLength
    }));
  };

  const enterReorderMode = () => {
    setReorderList([...products]);
    setReorderMode(true);
  };

  const cancelReorder = () => {
    setReorderMode(false);
    setReorderList([]);
  };

  const saveOrder = async () => {
    setSavingOrder(true);
    try {
      const token = localStorage.getItem('token');
      const order = reorderList.map((p, index) => ({ id: p._id, sortOrder: index }));
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/products/reorder`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ order }),
      });
      if (!res.ok) throw new Error('Failed to save order');
      toast.success('Product order saved!');
      setReorderMode(false);
      fetchProducts();
    } catch (err) {
      toast.error('Failed to save order');
    } finally {
      setSavingOrder(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
          <p className="text-slate-500 font-medium">Loading products...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Products</h1>
          <p className="text-slate-500 mt-1">{products.length} products in catalog</p>
        </div>
        <div className="flex items-center gap-3">
          {!reorderMode ? (
            <>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={enterReorderMode}
                className="bg-white border border-slate-200 text-slate-600 px-5 py-2.5 rounded-xl hover:bg-slate-50 font-medium flex items-center gap-2 shadow-sm transition-all"
              >
                <FaSort className="text-sm" />
                <span>Reorder</span>
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => {
                  setEditingProduct(null);
                  setIsModalOpen(true);
                }}
                className="bg-gradient-to-r from-primary-500 to-primary-600 text-white px-5 py-2.5 rounded-xl hover:from-primary-600 hover:to-primary-700 font-medium flex items-center gap-2 shadow-lg shadow-primary-500/30 transition-all"
              >
                <FaPlus className="text-sm" />
                <span>Add Product</span>
              </motion.button>
            </>
          ) : (
            <>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={cancelReorder}
                disabled={savingOrder}
                className="bg-white border border-slate-200 text-slate-600 px-5 py-2.5 rounded-xl hover:bg-slate-50 font-medium flex items-center gap-2 shadow-sm transition-all disabled:opacity-50"
              >
                <FaTimes className="text-sm" />
                <span>Cancel</span>
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={saveOrder}
                disabled={savingOrder}
                className="bg-gradient-to-r from-green-500 to-green-600 text-white px-5 py-2.5 rounded-xl hover:from-green-600 hover:to-green-700 font-medium flex items-center gap-2 shadow-lg shadow-green-500/30 transition-all disabled:opacity-50"
              >
                <FaSave className="text-sm" />
                <span>{savingOrder ? 'Saving...' : 'Save Order'}</span>
              </motion.button>
            </>
          )}
        </div>
      </div>

      {/* Product Form Modal */}
      <ProductForm
        product={editingProduct}
        isOpen={isModalOpen}
        onSuccess={() => {
          fetchProducts();
          setEditingProduct(null);
          setIsModalOpen(false);
        }}
        onClose={() => {
          setEditingProduct(null);
          setIsModalOpen(false);
        }}
      />

      {/* Reorder Mode */}
      <AnimatePresence>
        {reorderMode && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-3 mb-6 flex items-center gap-3">
              <FaSort className="text-amber-500 flex-shrink-0" />
              <p className="text-amber-700 text-sm font-medium">
                Drag products to set their display order. Position 1 appears first on the storefront.
              </p>
            </div>

            <Reorder.Group axis="y" values={reorderList} onReorder={setReorderList} className="space-y-3">
              {reorderList.map((prod, index) => {
                const images = prod.imageFilenames && prod.imageFilenames.length > 0
                  ? prod.imageFilenames
                  : prod.imageFilename ? [prod.imageFilename] : [];

                return (
                  <Reorder.Item
                    key={prod._id}
                    value={prod}
                    className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-4 cursor-grab active:cursor-grabbing shadow-sm hover:shadow-md transition-shadow select-none"
                  >
                    {/* Position number */}
                    <span className="w-8 h-8 flex items-center justify-center bg-slate-100 text-slate-600 font-bold rounded-lg text-sm flex-shrink-0">
                      {index + 1}
                    </span>

                    {/* Thumbnail */}
                    {images[0] ? (
                      <img
                        src={`${process.env.REACT_APP_API_URL}/api/images/${images[0]}`}
                        alt={prod.name}
                        className="w-14 h-14 rounded-lg object-cover flex-shrink-0 border border-slate-100"
                      />
                    ) : (
                      <div className="w-14 h-14 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                        <FaBox className="text-slate-400" />
                      </div>
                    )}

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-slate-800 truncate">{prod.name}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded">{prod.category}</span>
                        {prod.discount > 0 && (
                          <span className="text-xs text-red-600 font-semibold">-{prod.discount}% off</span>
                        )}
                      </div>
                    </div>

                    {/* Price */}
                    <div className="text-right flex-shrink-0 hidden sm:block">
                      <p className="font-bold text-slate-800">
                        ₹{prod.discount > 0 ? Math.round(prod.price * (1 - prod.discount / 100)) : prod.price}
                      </p>
                      {prod.discount > 0 && (
                        <p className="text-xs text-slate-400 line-through">₹{prod.price}</p>
                      )}
                    </div>

                    {/* Drag handle */}
                    <FaGripVertical className="text-slate-300 text-xl flex-shrink-0" />
                  </Reorder.Item>
                );
              })}
            </Reorder.Group>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Products Grid (normal mode) */}
      {!reorderMode && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {products.map((prod, index) => {
            const images = prod.imageFilenames && prod.imageFilenames.length > 0
              ? prod.imageFilenames
              : prod.imageFilename
                ? [prod.imageFilename]
                : [];
            const currentIndex = imageIndexes[prod._id] || 0;

            return (
              <motion.div
                key={prod._id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                whileHover={{ y: -4 }}
                className="relative bg-white rounded-xl shadow-card border border-slate-100 overflow-hidden hover:shadow-soft transition-all duration-300"
              >
                {/* Position badge */}
                <div className="absolute top-3 left-3 z-10 flex flex-col gap-2">
                  <div className="bg-slate-800/70 backdrop-blur-sm text-white px-2 py-0.5 rounded text-xs font-bold">
                    #{index + 1}
                  </div>
                  {prod.customizable && (
                    <div className="bg-gradient-to-r from-violet-500 to-purple-500 text-white px-2.5 py-1 rounded-lg text-xs font-semibold flex items-center gap-1 shadow-lg">
                      <FaStar className="text-yellow-300 text-[10px]" />
                      <span>Customizable</span>
                    </div>
                  )}
                  <div className="bg-slate-800/80 backdrop-blur-sm text-white px-2.5 py-1 rounded-lg text-xs font-medium">
                    {prod.category}
                  </div>
                </div>

                {/* Discount Badge */}
                {prod.discount > 0 && (
                  <div className="absolute top-3 right-3 z-10 bg-red-500 text-white px-2.5 py-1 rounded-lg text-xs font-bold shadow-lg">
                    -{prod.discount}%
                  </div>
                )}

                {/* Image Carousel */}
                <div className="relative w-full h-52 bg-slate-100">
                  <AnimatePresence mode="wait">
                    <motion.img
                      key={currentIndex}
                      src={`${process.env.REACT_APP_API_URL}/api/images/${images[currentIndex]}`}
                      alt={prod.name}
                      className="w-full h-full object-cover"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.2 }}
                    />
                  </AnimatePresence>

                  {images.length > 1 && (
                    <>
                      <button
                        onClick={(e) => { e.stopPropagation(); prevImage(prod._id, images.length); }}
                        className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-2 shadow-md transition-all"
                      >
                        <FaChevronLeft className="text-slate-700 text-sm" />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); nextImage(prod._id, images.length); }}
                        className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-2 shadow-md transition-all"
                      >
                        <FaChevronRight className="text-slate-700 text-sm" />
                      </button>
                      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                        {images.map((_, idx) => (
                          <button
                            key={idx}
                            onClick={(e) => { e.stopPropagation(); setImageIndexes(prev => ({ ...prev, [prod._id]: idx })); }}
                            className={`h-1.5 rounded-full transition-all ${idx === currentIndex ? 'bg-white w-4' : 'bg-white/50 w-1.5'}`}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </div>

                {/* Product Info */}
                <div className="p-5">
                  <h3 className="font-semibold text-slate-800 mb-1 line-clamp-1">{prod.name}</h3>
                  <p className="text-sm text-slate-500 mb-3 line-clamp-2">{prod.description}</p>

                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-baseline gap-2">
                      <span className="text-xl font-bold text-slate-800">
                        ₹{prod.discount > 0 ? Math.round(prod.price * (1 - prod.discount / 100)) : prod.price}
                      </span>
                      {prod.discount > 0 && (
                        <span className="text-sm text-slate-400 line-through">₹{prod.price}</span>
                      )}
                    </div>
                    {prod.deliveryTime && (
                      <span className="text-xs text-slate-400 bg-slate-50 px-2 py-1 rounded">{prod.deliveryTime}</span>
                    )}
                  </div>

                  {prod.colors && prod.colors.length > 0 && (
                    <div className="mb-4">
                      <div className="flex flex-wrap gap-1.5">
                        {prod.colors.slice(0, 3).map((color, idx) => (
                          <span key={idx} className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-md">{color}</span>
                        ))}
                        {prod.colors.length > 3 && (
                          <span className="text-xs bg-slate-100 text-slate-500 px-2 py-1 rounded-md">+{prod.colors.length - 3} more</span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex gap-2 pt-3 border-t border-slate-100">
                    <button
                      onClick={() => { setEditingProduct(prod); setIsModalOpen(true); }}
                      className="flex-1 bg-primary-50 text-primary-600 hover:bg-primary-100 px-4 py-2.5 rounded-lg flex items-center justify-center gap-2 font-medium transition-colors"
                    >
                      <FaEdit className="text-sm" />
                      <span>Edit</span>
                    </button>
                    <button
                      onClick={() => handleDelete(prod._id)}
                      className="flex-1 bg-red-50 text-red-600 hover:bg-red-100 px-4 py-2.5 rounded-lg flex items-center justify-center gap-2 font-medium transition-colors"
                    >
                      <FaTrash className="text-sm" />
                      <span>Delete</span>
                    </button>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {products.length === 0 && !reorderMode && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center py-16 bg-white rounded-xl shadow-card border border-slate-100"
        >
          <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FaBox className="text-2xl text-slate-400" />
          </div>
          <h3 className="text-lg font-semibold text-slate-700 mb-2">No products yet</h3>
          <p className="text-slate-500 mb-4">Add your first product to get started</p>
          <button
            onClick={() => { setEditingProduct(null); setIsModalOpen(true); }}
            className="bg-primary-600 text-white px-5 py-2.5 rounded-lg font-medium hover:bg-primary-700 transition-colors"
          >
            Add Product
          </button>
        </motion.div>
      )}
    </div>
  );
};

export default Products;
